import { CdkDragDrop, DragDropModule, moveItemInArray, transferArrayItem } from '@angular/cdk/drag-drop';
import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  ElementRef,
  HostListener,
  inject,
  input,
  model,
  OnDestroy,
  OnInit,
  Optional,
  output,
  Self,
  signal,
  TemplateRef,
  untracked,
  viewChild,
  viewChildren,
  ViewEncapsulation,
} from '@angular/core';
import { AbstractControl, FormsModule, NgControl, ValidationErrors, Validator, ValidatorFn } from '@angular/forms';
import { HxGridTabNavigation } from '../../a11y';
import { HxBaseControl } from '../../base/hx-base-control';
import { HxAlert } from '../alert/alert';
import { HxCheckbox } from '../checkbox/checkbox';
import { HxCheckboxWithLabel } from '../checkbox/checkbox-with-label/checkbox-with-label';
import { HxIcon } from '../icon/icon';
import { HxInput } from '../input/input';
import { HxLabel } from '../label/label';
import { HxPagination } from '../pagination';
import { PaginationEvent } from '../pagination/pagination-event';
import { RowsPerPageOption } from '../pagination/rows-per-page-option';
import { HxRadio } from '../radio/radio';
import { HxTableSectionComponent } from '../table/directives';
import { HxTableCellDirective } from '../table/directives/table-cell';
import { HxTableFooterDirective } from '../table/directives/table-footer';
import { HxTableHeaderDirective } from '../table/directives/table-header';
import { HxTableRowDirective } from '../table/directives/table-row';
import { HxHeaderCell } from '../table/header-cell';
import { HxTableComponent } from '../table/table';
import {
  CheckboxRowValue,
  ColumnAlignment,
  ColumnDefinition,
  InputType,
  PrimitiveValue,
  ResponseOutput,
  RowData,
  RowValidationError,
  RowValue,
  SectionData,
  TableResponses,
  TextRowValue,
  ValidationMode,
  ValidationResult,
} from './types';

/**
 * Harmony Table Grid - Configuration-driven matrix control
 *
 * A flexible, multi-input table component for collecting structured data across rows and columns. Supports radio
 * buttons, checkboxes, and text inputs with comprehensive validation and pagination.
 *
 * ## Key Features
 *
 * - **Multi-input Types:** Radio, checkbox, or text input modes for each table
 * - **Validation:** All-rows, at-least-one-yes, custom, or no validation modes
 * - **Conditional Display:** Show/hide rows based on cumulative response scores
 * - **Pagination:** Built-in support for large datasets with configurable page sizes
 * - **Reactive Forms:** ControlValueAccessor integration for form control binding
 * - **Sticky Header:** Optional fixed header during vertical scrolling
 * - **Responsive:** Automatically adjusts column widths and spacing
 * - **Accessibility:** ARIA labels, roles, and keyboard navigation support
 *
 * ## Usage
 *
 * ```html
 * <hx-table-grid
 *   [rows]="rows"
 *   [columns]="columns"
 *   inputType="radio"
 *   [requiredMode]="'all-rows'"
 *   [stickyHeader]="true"
 *   questionLabel="Assessment Questions"
 *   [formControl]="control"
 *   (responseChange)="onResponse($event)">
 * </hx-table-grid>
 * ```
 *
 * ## Validation Modes
 *
 * - **none:** No validation (all-valid by default)
 * - **all-rows:** All rows must have a response
 * - **at-least-one-yes:** At least one row must have value = 1 (or true for checkbox)
 * - **custom:** Per-row required flags determine validity
 *
 * ## Data Structure
 *
 * - **Rows:** Array of RowData with labels and optional conditions
 * - **Columns:** Array of ColumnDefinition with labels and numeric values
 * - **Responses:** TableResponses object mapping row IDs to values
 *
 * @example
 *   ```typescript
 *   // Component with radio selection
 *   rows = [
 *     { linkId: 'q1', label: 'Risk Factor 1' },
 *     { linkId: 'q2', label: 'Risk Factor 2' }
 *   ];
 *   columns = [
 *     { id: 'no', label: 'No', value: 0 },
 *     { id: 'yes', label: 'Yes', value: 1 }
 *   ];
 *   ```;
 *
 * @class
 * @extends HxBaseControl
 * @implements OnInit
 * @implements Validator
 */
@Component({
  selector: 'hx-table-grid',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    DragDropModule,
    HxTableComponent,
    HxTableFooterDirective,
    HxTableRowDirective,
    HxTableCellDirective,
    HxTableHeaderDirective,
    HxHeaderCell,
    HxRadio,
    HxLabel,
    HxCheckbox,
    HxCheckboxWithLabel,
    HxInput,
    HxPagination,
    HxAlert,
    HxTableSectionComponent,
    HxIcon,
    HxGridTabNavigation,
  ],
  templateUrl: './table-grid.html',
  styleUrls: ['./table-grid.css'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[class]': 'hostClasses()',
    '[attr.role]': '"table"',
  },
})
export class HxTableGrid extends HxBaseControl implements OnInit, AfterViewInit, OnDestroy, Validator {
  private _elementRef = inject(ElementRef);

  // --- CORE DATA INPUTS ---
  readonly sections = input<SectionData[]>([]);
  readonly columns = input<ColumnDefinition[]>([]);

  // --- PAGINATION INPUTS - Optional ---
  /**
   * Total number of items across all pages Drives pagination calculations If not provided, defaults to rows.length
   *
   * @default rows.length
   */
  readonly totalRecordCount = input<number | undefined>(undefined);

  // --- PAGINATION INPUTS - Optional Configuration ---
  /**
   * Available page size options for dropdown
   *
   * @default [10, 25, 50, 100]
   */
  readonly pageSizeOptions = input<RowsPerPageOption[]>([
    { id: 'page-size-10', value: 10, label: '10' },
    { id: 'page-size-25', value: 25, label: '25' },
    { id: 'page-size-50', value: 50, label: '50' },
    { id: 'page-size-100', value: 100, label: '100' },
  ]);

  /**
   * Initial page size Used only in uncontrolled mode; ignored if [(pageSize)] is bound If not in pageSizeOptions, will
   * default to first option
   *
   * @default 10
   */
  readonly initialPageSize = input<number>(10);

  /**
   * Label text for page size selector
   *
   * @default 'Rows per page:'
   */
  readonly rowsPerPageLabel = input<string>('Rows per page:');

  /**
   * Label text for go-to-page input
   *
   * @default 'Go to page'
   */
  readonly gotoPageLabel = input<string>('Go to page');

  /**
   * Show or hide the go-to-page input section
   *
   * @default true
   */
  readonly showGotoPage = input<boolean>(true);

  /**
   * Error message to display when go-to-page input is invalid
   *
   * @default 'Page number is invalid'
   */
  readonly gotoPageErrorMessage = input<string>('Page number is invalid');

  // --- TABLE GRID CONFIGURATION INPUTS ---
  readonly tableId = input<string>('hx-table-grid');
  readonly inputType = input<InputType>('radio');
  readonly questionLabel = input<string>('Question');

  /**
   * Unique FHIR linkId for this control instance. Used to key the "Configure Columns" visibility preference in
   * localStorage so each dropped table control retains its own settings.
   */
  readonly linkId = input<string | undefined>(undefined);

  /**
   * Whether to show the "Configure Columns" trigger/popover, letting users hide/show columns. Backward-compatible: when
   * this property is not provided (older saved templates), defaults to true.
   *
   * @default true
   */
  readonly showConfigureColumns = input<boolean>(true);

  /**
   * Whether column widths resized by the user via drag should be saved (alongside hidden columns) and restored on
   * reopen. Backward-compatible: when this property is not provided (older saved templates), defaults to true.
   *
   * @default true
   */
  readonly saveColumnWidths = input<boolean>(true);

  /**
   * Designer-configurable default for whether the header row starts pinned while the body scrolls. End users can still
   * toggle this via the "Configure Columns" menu; their choice is persisted and takes precedence over this default.
   *
   * @default false
   */
  readonly freezeHeader = input<boolean>(false);

  /** Cap applied to the scrollable table body while the header is frozen (passed through to `hx-table`). */
  readonly freezeHeaderMaxHeight = input<string>('min(60vh, 36rem)');

  // Sticky Inputs
  readonly stickyHeader = input<boolean>(true);

  // Layout
  readonly showHeader = input<boolean>(true);
  readonly showSerialNumber = input<boolean>(true);
  readonly showRowActions = input<boolean>(false);
  /** Width of the row-actions column when showRowActions is true. */
  readonly rowActionsColumnWidth = input<string>('20%');
  readonly enableRowHover = input<boolean>(false);
  readonly enableRowReordering = input<boolean>(false);
  readonly resizableColumns = input<boolean>(false);
  readonly showTotalCount = input<boolean>(false);
  readonly enableColumnSelectAll = input<boolean>(false);
  readonly selectAllColumnIds = input<string[]>([]);
  readonly cssClass = input<string | undefined>(undefined);
  readonly serialNumberWidth = input<string>('5%');
  readonly questionLabelWidth = input<string>('40%');

  // Total Row Styling
  readonly totalBold = input<boolean>(false);
  readonly totalFontColor = input<string | undefined>(undefined);
  readonly totalBackgroundColor = input<string | undefined>(undefined);
  readonly totalPosition = input<'left' | 'right'>('left');

  // Row Styling
  readonly rowBackgroundColor = input<string | undefined>(undefined);
  readonly alternateRowColor = input<string | undefined>(undefined);

  // Row Outline Control
  readonly showRowOutline = input<boolean>(true);

  // Validation
  readonly requiredMode = input<ValidationMode>('all-rows');
  readonly validationTargetValue = input<string | undefined>(undefined);
  readonly validationMessage = input<string | undefined>(undefined);
  readonly forceShowErrors = input<boolean>(false);

  // Conditional Display
  /**
   * Minimum score threshold required to show conditional rows (rows with showDefault: false). Hidden rows will be shown
   * only when the aggregated score from default-shown rows meets or exceeds this value AND all default-shown rows have
   * values.
   *
   * @default undefined (no conditional display)
   */
  readonly conditionalScore = input<number | undefined>(undefined);

  // Alignment
  readonly headerAlignment = input<ColumnAlignment>('center');
  readonly bodyAlignment = input<ColumnAlignment>('center');
  readonly ariaLabel = input<string>('Data table');

  // Section Grouping
  readonly enableSectionMode = input<boolean>(false);
  readonly secondaryIndexFormat = input<'numeric' | 'alphabetic'>('numeric');

  // Templates
  readonly questionLabelTemplate = input<TemplateRef<{ $implicit: string }> | undefined>(undefined);
  readonly rowLabelTemplate = input<TemplateRef<{ $implicit: RowData; required: boolean }> | undefined>(undefined);
  readonly columnTemplate = input<TemplateRef<{ $implicit: ColumnDefinition; index: number }> | undefined>(undefined);
  readonly rowActionsTemplate = input<TemplateRef<{ $implicit: RowData; required: boolean }> | undefined>(undefined);
  readonly sectionLabelTemplate = input<TemplateRef<{ $implicit: string | null; index: number }> | undefined>(
    undefined
  );

  // Label configuration
  readonly labelColor = input<string | undefined>(undefined);
  readonly label = input<string | undefined>(undefined);
  readonly labelRequired = input<boolean>(false);
  readonly labelSubtitle = input<string | undefined>(undefined);
  readonly labelFontWeight = input<string | undefined>(undefined);
  readonly labelFontSize = input<string | undefined>(undefined);
  readonly subtitleFontWeight = input<string | undefined>(undefined);
  readonly subtitleFontSize = input<string | undefined>(undefined);
  readonly subtitleColor = input<string | undefined>(undefined);

  // --- PAGINATION MODELS - Two-Way Bindings for Controlled Mode ---
  /**
   * Current page number (1-indexed) Can be bound with [(currentPage)]="mySignal" for parent-controlled mode
   * Uncontrolled mode: Initialized from initialPage input
   */
  readonly currentPage = model<number>(1);

  /**
   * Current page size Can be bound with [(pageSize)]="mySignal" for parent-controlled mode Uncontrolled mode:
   * Initialized from initialPageSize input
   */
  readonly pageSize = model<number | null>(null);

  // --- OUTPUTS ---
  readonly responseChange = output<ResponseOutput>();

  /** Emitted when rows are reordered */
  readonly rowsReordered = output<RowData[]>();

  /** Emitted when page number changes */
  readonly pageNumberChange = output<PaginationEvent>();

  /** Emitted when page size changes */
  readonly pageSizeChange = output<PaginationEvent>();

  /** Emitted when the user toggles "Freeze Header" via the Configure Columns menu */
  readonly freezeHeaderChange = output<boolean>();

  // View Child
  readonly tableComponent = viewChild(HxTableComponent);

  readonly radioButtons = viewChildren(HxRadio);

  readonly checkboxInstances = viewChildren(HxCheckboxWithLabel);

  // --- STATE SIGNALS ---
  protected readonly rowResponses = signal<TableResponses>({});
  protected readonly isTouched = signal<boolean>(false);
  protected readonly conditionalRowsShown = signal<boolean>(false); // Tracks conditional rows visibility state

  // CSS Var Signals
  protected readonly gridTemplateColumns = signal<string>('1fr');

  // --- CONFIGURE COLUMNS STATE ---
  /** Set of column ids currently hidden by the user, keyed to localStorage by linkId/tableId. */
  protected readonly hiddenColumnIds = signal<Set<string>>(new Set());
  /** Drag-resized column widths keyed by column id, persisted alongside hidden columns when `saveColumnWidths` is true. */
  protected readonly columnWidthOverrides = signal<Record<string, string>>({});
  protected readonly showColumnConfig = signal<boolean>(false);
  protected readonly columnConfigPosition = signal<{ top: number; left: number }>({ top: 0, left: 0 });

  /** Runtime override set via the "Configure Columns" toggle; null means fall back to the `freezeHeader` input default. */
  protected readonly freezeHeaderOverride = signal<boolean | null>(null);

  /** Effective frozen state: the user's override if set, otherwise the designer-configured default. */
  protected readonly headerFrozen = computed(() => this.freezeHeaderOverride() ?? this.freezeHeader());

  /** Columns filtered down to only the ones the user has chosen to keep visible. */
  protected readonly visibleColumns = computed(() => {
    const hidden = this.hiddenColumnIds();
    if (hidden.size === 0) return this.columns();
    return this.columns().filter((col) => !hidden.has(col.id));
  });

  /** True when the user has hidden at least one column (drives the "configured" indicator). */
  protected readonly hasCustomColumnConfig = computed(() => this.hiddenColumnIds().size > 0);

  /** Which existing header/body cell hosts the "Configure Columns" trigger (no dedicated column is added). */
  protected readonly configureColumnsHost = computed<'serial' | 'rowActions' | 'questionLabel'>(() => {
    if (this.showSerialNumber()) return 'serial';
    if (this.showRowActions()) return 'rowActions';
    return 'questionLabel';
  });

  /** Minimum width applied to whichever cell hosts the "Configure Columns" button so it never gets clipped. */
  private static readonly CONFIGURE_COLUMNS_MIN_WIDTH = '65px';

  /** Floors a column width to leave room for the "Configure Columns" button when it hosts it. */
  private _withConfigureColumnsFloor(width: string, host: 'serial' | 'rowActions' | 'questionLabel'): string {
    if (this.showConfigureColumns() && this.configureColumnsHost() === host) {
      return `max(${width}, ${HxTableGrid.CONFIGURE_COLUMNS_MIN_WIDTH})`;
    }
    return width;
  }

  /** Effective serial-number column width, floored when it hosts the Configure Columns button. */
  protected readonly effectiveSerialNumberWidth = computed(() =>
    this._withConfigureColumnsFloor(this.serialNumberWidth(), 'serial')
  );

  /** Effective row-actions column width, floored when it hosts the Configure Columns button. */
  protected readonly effectiveRowActionsWidth = computed(() =>
    this._withConfigureColumnsFloor(this.rowActionsColumnWidth(), 'rowActions')
  );

  /** Effective question-label column width, floored when it hosts the Configure Columns button. */
  protected readonly effectiveQuestionLabelWidth = computed(() =>
    this._withConfigureColumnsFloor(this.questionLabelWidth(), 'questionLabel')
  );

  // --- STATE: Validation Mode Warning ---
  protected readonly invalidValidationModeWarning = signal<string | null>(null);

  // --- COMPUTED: Drop List IDs for connected sections ---
  protected readonly dropListIds = computed(() => {
    return this.sections().map((_, index) => `section-drop-list-${index}`);
  });

  // Helper to safely get a drop list ID
  getDropListId(index: number): string {
    return this.dropListIds()[index] || `section-drop-list-${index}`;
  }

  // Helper to format secondary index based on format type
  getSecondaryIndex(rowIndex: number): string {
    if (this.secondaryIndexFormat() === 'alphabetic') {
      // Convert 0→a, 1→b, 2→c, etc. (handles multiple sets of 26)
      const char = String.fromCharCode(97 + (rowIndex % 26));
      const set = Math.floor(rowIndex / 26);
      return set > 0 ? char + set : char;
    }
    return (rowIndex + 1).toString();
  }

  // --- COMPUTED: Effective Total Record Count ---
  protected readonly effectiveTotalRecordCount = computed(() => {
    const provided = this.totalRecordCount();
    return provided !== undefined ? provided : this.visibleRows().length; // Use visible rows
  });

  // --- COMPUTED: Mixed Label Detection ---
  protected readonly columnsWithMixedLabels = computed(() => {
    const cols = this.columns();
    const allRows = this.getAllRows();
    const mixedSet = new Set<string>();

    cols.forEach((col) => {
      if (!col.cellLabel) {
        const hasAnyRowLabel = allRows.some((r) => !!r.cellLabels?.[col.id]);
        if (hasAnyRowLabel) {
          mixedSet.add(col.id);
        }
      }
    });
    return mixedSet;
  });

  // --- COMPUTED: Flattened rows from all sections ---
  protected readonly allRows = computed(() => {
    const sections = this.sections();
    if (!sections || sections.length === 0) return [];
    return sections.flatMap((s) => s.rows || []);
  });

  // --- COMPUTED: Row Filtering for Conditional Display ---
  /** Rows that are shown by default (showDefault !== false) */
  protected readonly defaultRows = computed(() => {
    return this.allRows().filter((row) => row.showDefault !== false);
  });

  /** Rows that are conditionally shown (showDefault === false) */
  protected readonly conditionalRows = computed(() => {
    return this.allRows().filter((row) => row.showDefault === false);
  });

  getValueScore = (val: any): number => {
    if (typeof val === 'number') return val;
    if (typeof val === 'string') {
      const lower = val.toLowerCase().trim();
      if (lower === 'yes') return 1;
      if (lower === 'no') return 0;
      if (lower === 'maybe' || lower === 'unsure') return 0.5;
      const num = parseFloat(val);
      return isNaN(num) ? 0 : num;
    }
    return 0;
  };
  /** Score calculated only from default-shown rows */
  protected readonly defaultRowsScore = computed(() => {
    const defaultRowIds = new Set(this.defaultRows().map((r) => r.linkId));
    const responses = this.rowResponses();
    let score = 0;

    if (this.inputType() === 'radio' || this.inputType() === 'text') {
      Object.entries(responses).forEach(([rowId, val]: [string, RowValue]) => {
        if (defaultRowIds.has(rowId) && val !== undefined && val !== null) {
          score += this.getValueScore(val);
        }
      });
    }

    if (this.inputType() === 'checkbox') {
      const colScoreMap = new Map<string, number>();
      this.columns().forEach((c) => {
        colScoreMap.set(c.id, this.getValueScore(c.value));
      });

      Object.entries(responses).forEach(([rowId, val]) => {
        if (defaultRowIds.has(rowId)) {
          const rowSelection = val as CheckboxRowValue;
          if (rowSelection && typeof rowSelection === 'object') {
            Object.keys(rowSelection).forEach((colId) => {
              if (rowSelection[colId] === true) {
                score += colScoreMap.get(colId) || 0;
              }
            });
          }
        }
      });
    }

    return score;
  });

  /** Check if all default rows have values */
  protected readonly allDefaultRowsCompleted = computed(() => {
    const defaultRowIds = new Set(this.defaultRows().map((r) => r.linkId));
    const responses = this.rowResponses();

    return this.defaultRows().every((row) => {
      const val = responses[row.linkId];
      return this._hasAnyValue(val);
    });
  });

  /** Should conditional rows be shown based on score and completion criteria */
  protected readonly shouldShowConditionalRows = computed(() => {
    const threshold = this.conditionalScore();
    if (threshold === undefined) return true; // No conditional logic

    const currentScore = this.defaultRowsScore();
    const allCompleted = this.allDefaultRowsCompleted();

    return currentScore >= threshold && allCompleted;
  });

  /** All rows that should be visible (default + conditional if criteria met) */
  protected readonly visibleRows = computed(() => {
    const defaultRows = this.defaultRows();
    const conditionalRows = this.conditionalRows();

    if (this.shouldShowConditionalRows()) {
      return [...defaultRows, ...conditionalRows];
    }

    return defaultRows;
  });

  getCellLabel(row: RowData, col: ColumnDefinition): string {
    if (row.cellLabels?.[col.id]) return row.cellLabels[col.id];
    if (col.cellLabel) return col.cellLabel;
    if (this.columnsWithMixedLabels().has(col.id)) return col.label;
    return '';
  }

  /** Get all rows from all sections */
  private getAllRows(): RowData[] {
    return this.allRows();
  }

  /** Get background color for a row based on index and settings */
  getRowBackgroundColor(index: number): string | undefined {
    const baseColor = this.rowBackgroundColor();
    const altColor = this.alternateRowColor();

    if (altColor && index % 2 === 1) {
      return altColor;
    }
    return baseColor;
  }

  /** Check if all visible rows (including conditional rows) have a specific column selected */
  isColumnSelectedForAll(columnValue: PrimitiveValue): boolean {
    const responses = this.rowResponses();
    const rows = this.visibleRows(); // Use all visible rows instead of just paginated

    if (rows.length === 0) return false;

    if (this.inputType() === 'checkbox') {
      const columnId = String(columnValue);
      return rows.every((row) => {
        const response = responses[row.linkId] as CheckboxRowValue | undefined;
        return response?.[columnId] === true;
      });
    }

    return rows.every((row) => {
      const response = responses[row.linkId];
      return response === columnValue;
    });
  }

  /** Current header select-all value when all visible rows share the same column selection. */
  protected readonly selectAllHeaderValue = computed<PrimitiveValue | null>(() => {
    if (!this.enableColumnSelectAll() || !this.isColumnSelectAllEligible()) {
      return null;
    }

    const rows = this.visibleRows();
    if (rows.length === 0) {
      return null;
    }

    const responses = this.rowResponses();
    const firstRow = rows[0];
    if (!firstRow) {
      return null;
    }

    const firstRowValue = responses[firstRow.linkId] as PrimitiveValue | undefined;

    if (firstRowValue === undefined || firstRowValue === null || firstRowValue === '') {
      return null;
    }

    const allRowsMatch = rows.every((row) => responses[row.linkId] === firstRowValue);
    if (!allRowsMatch) {
      return null;
    }

    return this.columns().some((col) => (col.value ?? col.id) === firstRowValue) ? firstRowValue : null;
  });

  /** Computed map of select-all states per column - tracks which columns have all rows selected */
  protected readonly selectAllStatesPerColumn = computed<Record<string, boolean>>(() => {
    if (!this.enableColumnSelectAll()) {
      return {};
    }

    const states: Record<string, boolean> = {};
    const responses = this.rowResponses();
    const rows = this.visibleRows();

    for (const col of this.columns()) {
      if (!this.isColumnSelectAllEligible()) {
        continue;
      }

      if (rows.length === 0) {
        states[String(col.value || col.id)] = false;
        continue;
      }

      // Check if all visible rows have this column selected
      const allSelected = this.isColumnSelectedForAll(col.value || col.id);
      states[String(col.value || col.id)] = allSelected;
    }

    return states;
  });

  protected shouldShowSelectAllForColumn(columnId: string): boolean {
    if (!this.enableColumnSelectAll() || !this.isColumnSelectAllEligible()) {
      return false;
    }

    const selectedColumnIds = this.selectAllColumnIds();
    if (selectedColumnIds.length === 0) {
      return true;
    }

    return selectedColumnIds.includes(columnId);
  }

  /** Helper method to get the select-all state key for a column */
  getSelectAllStateKey(col: ColumnDefinition): string {
    return String(col.value || col.id);
  }

  /** Per-instance scope for the select-all header radio group name/id. */
  private get _selectAllScopeId(): string {
    return this.linkId() || this.tableId();
  }

  /** Unique native `name` for a column's select-all header radio, scoped to this table-grid instance. */
  protected getSelectAllRadioName(col: ColumnDefinition): string {
    return this.inputType() === 'checkbox'
      ? `${this._selectAllScopeId}-select-all-header-checkbox-${col.id}`
      : `${this._selectAllScopeId}-select-all-header-${col.value || col.id}`;
  }

  /** Unique DOM `id` for a column's select-all header radio, scoped to this table-grid instance. */
  protected getSelectAllRadioId(col: ColumnDefinition): string {
    return this.inputType() === 'checkbox'
      ? `${this._selectAllScopeId}-hx-table-grid-select-all-checkbox-${col.id}`
      : `${this._selectAllScopeId}-hx-table-grid-select-all-${col.value || col.id}`;
  }

  protected isColumnSelectAllEligible(): boolean {
    if (this.inputType() == 'radio' || this.inputType() == 'checkbox') {
      return true;
    }
    return false;
  }

  /** Select all visible rows (including conditional rows) for a specific column */
  selectAllForColumn(columnValue: PrimitiveValue): void {
    if (this.disabledState()) return;

    const rows = this.visibleRows(); // Use all visible rows instead of just paginated

    // Check if all are already selected
    const allSelected = this.isColumnSelectedForAll(columnValue);

    this.rowResponses.update((current) => {
      const updated = { ...current };

      rows.forEach((row) => {
        if (this.inputType() === 'checkbox') {
          const columnId = String(columnValue);
          const existingRow = (updated[row.linkId] as CheckboxRowValue) || {};
          updated[row.linkId] = { ...existingRow, [columnId]: !allSelected };
        } else {
          if (allSelected) {
            // Deselect all - clear the selection
            delete updated[row.linkId];
          } else {
            // Select all for this column
            updated[row.linkId] = columnValue;
          }
        }
      });

      return updated;
    });

    this._propagateChanges();
  }

  /** Clear native checked state for this instance's header select-all radio group. */
  private clearHeaderSelectAllSelection(): void {
    const radios = this._elementRef.nativeElement.querySelectorAll(
      `input[type="radio"][name*="${this._selectAllScopeId}-select-all-header-"]`
    );
    radios.forEach((radio: HTMLInputElement) => {
      radio.checked = false;
    });
  }

  /** Get effective column width based on saved override, provided width, or input type defaults */
  getColumnWidth(col: ColumnDefinition): string {
    const override = this.columnWidthOverrides()[col.id];
    if (override) {
      return override;
    }
    const width = (col as ColumnDefinition & { width?: unknown }).width;
    if (typeof width === 'number' && Number.isFinite(width)) {
      if (width > 0) {
        return `${width}px`;
      }
    }
    if (typeof width === 'string' && width.trim()) {
      return width;
    }
    // Default width based on input type
    if (this.inputType() === 'radio' || this.inputType() === 'checkbox') {
      return '120px';
    }
    return '300px'; // default for text input type
  }

  /**
   * Build the CSS grid-template-columns string from all configured column widths. Reads signal values directly (safe to
   * call both inside and outside effects). When called inside an effect, all signal reads are tracked as reactive
   * dependencies.
   */
  private _buildGridWidths(): string {
    const widths: string[] = [];
    const cols = this.visibleColumns();

    // 1. Serial Number Column
    if (this.showSerialNumber()) widths.push(this.effectiveSerialNumberWidth());

    // 2. Row Actions Column
    if (this.showRowActions()) widths.push(this.effectiveRowActionsWidth());

    // 3. Question Label Column
    widths.push(this.effectiveQuestionLabelWidth());

    // 4. Data Columns — use explicitly saved width or fall back to input-type default
    cols.forEach((col) => {
      widths.push(this.getColumnWidth(col));
    });

    return widths.join(' ');
  }

  // --- COMPUTED: Scoring ---
  protected readonly totalScore = computed(() => {
    if (!this.showTotalCount()) return 0;

    const visibleRowIds = new Set(this.visibleRows().map((r) => r.linkId));
    const responses = this.rowResponses();
    let score = 0;

    if (this.inputType() === 'radio' || this.inputType() === 'text') {
      Object.entries(responses).forEach(([rowId, val]: [string, RowValue]) => {
        if (visibleRowIds.has(rowId) && val !== undefined && val !== null) {
          score += this.getValueScore(val);
        }
      });
    }

    if (this.inputType() === 'checkbox') {
      const colScoreMap = new Map<string, number>();
      this.columns().forEach((c) => {
        colScoreMap.set(c.id, this.getValueScore(c.value));
      });

      Object.entries(responses).forEach(([rowId, val]) => {
        if (visibleRowIds.has(rowId)) {
          const rowSelection = val as CheckboxRowValue;
          if (rowSelection && typeof rowSelection === 'object') {
            Object.keys(rowSelection).forEach((colId) => {
              if (rowSelection[colId] === true) {
                score += colScoreMap.get(colId) || 0;
              }
            });
          }
        }
      });
    }

    return score;
  });

  // --- COMPUTED: Pagination ---
  protected readonly paginatedRows = computed(() => {
    const rows = this.visibleRows(); // Use visible rows instead of all rows
    const size = this.pageSize() ?? this.initialPageSize();
    const start = (this.currentPage() - 1) * size;
    return rows.slice(start, start + size);
  });

  protected readonly totalPages = computed(() => {
    const size = this.pageSize() ?? this.initialPageSize();
    return Math.ceil(this.visibleRows().length / size); // Use visible rows count
  });

  protected readonly pageOffset = computed(() => {
    return (this.currentPage() - 1) * (this.pageSize() ?? this.initialPageSize());
  });

  protected readonly hostClasses = computed(() => {
    const classes = ['hx-table-grid'];
    if (this.disabledState()) classes.push('hx-table-grid--disabled');
    return classes.join(' ');
  });

  constructor(@Self() @Optional() public ngControl: NgControl) {
    super();
    if (this.ngControl) {
      this.ngControl.valueAccessor = this;
    }

    // Drop any stale "Configure Columns" freeze-header override once the designer-configured default changes,
    // so properties-panel edits are reflected immediately instead of being masked by a persisted end-user choice.
    effect(() => {
      this.freezeHeader();
      untracked(() => {
        if (this.freezeHeaderOverride() !== null) {
          this.freezeHeaderOverride.set(null);
          this._persistColumnConfigState();
        }
      });
    });

    // --- EFFECT: Track conditional row visibility state ---
    effect(() => {
      const threshold = this.conditionalScore();
      if (threshold === undefined) {
        this.conditionalRowsShown.set(false);
        return;
      }

      const currentScore = this.defaultRowsScore();
      const allCompleted = this.allDefaultRowsCompleted();
      const shouldShow = currentScore >= threshold && allCompleted;

      // Update visibility flag based on current criteria
      this.conditionalRowsShown.set(shouldShow && this.conditionalRows().length > 0);
    });

    // --- EFFECT: Validate Validation Mode with Input Type ---
    effect(() => {
      const type = this.inputType();
      const mode = this.requiredMode();

      if (type === 'text' && mode === 'at-least-one-yes') {
        this.invalidValidationModeWarning.set(
          'Warning: "At least one yes" validation mode is not applicable for text input type. Text inputs can contain any value. Please select "all-rows" or "none" validation mode.'
        );
      } else {
        this.invalidValidationModeWarning.set(null);
      }
    });

    // --- EFFECT: Adjust currentPage if it exceeds totalPages ---
    effect(() => {
      const max = this.totalPages();
      const current = this.currentPage();
      if (current > max) {
        this.currentPage.set(Math.max(1, max));
      }
    });

    // --- EFFECT: Initialize pageSize from initialPageSize if not using two-way binding ---
    effect(() => {
      if (this.pageSize() !== null) {
        return;
      }

      const requestedSize = this.initialPageSize();
      const options = this.pageSizeOptions();
      const resolvedSize =
        options.find((opt) => opt.value === requestedSize)?.value ?? options[0]?.value ?? requestedSize;

      // Use requested size if it's in the options; otherwise fall back to the first option
      this.pageSize.set(resolvedSize);
    });

    // --- EFFECT: Initialize Defaults ---
    effect(() => {
      if (this.disabledState()) return;

      const currentRows = this.allRows();
      const currentCols = this.columns();
      const type = this.inputType();

      const currentData = untracked(this.rowResponses);
      if (Object.keys(currentData).length > 0) return;

      const defaults: TableResponses = {};

      currentRows.forEach((row) => {
        if (type === 'checkbox') {
          const rowObj: Record<string, boolean> = {};
          currentCols.forEach((col) => {
            rowObj[col.id] = false;
          });
          defaults[row.linkId] = rowObj;
        } else if (type === 'radio') {
          defaults[row.linkId] = undefined;
        } else if (type === 'text') {
          const rowObj: Record<string, string> = {};
          currentCols.forEach((col) => (rowObj[col.id] = ''));
          defaults[row.linkId] = rowObj;
        }
      });

      this.rowResponses.set(defaults);
      this._propagateChanges();
    });

    // --- EFFECT: CSS Grid Layout ---
    effect(() => {
      // Build grid template columns from all configured widths.
      // Signal reads here (columns, showSerialNumber, etc.) are tracked as reactive dependencies.
      const widths = this._buildGridWidths();

      // Update the local gridTemplateColumns signal
      this.gridTemplateColumns.set(widths);

      const table = this.tableComponent();
      if (table) {
        table.updateGridColumns(widths);
      }
    });

    // Sync radio button checked states with responses
    // Sync select-all header radio states with responses. Cell radios are not handled here: their checked state is
    // bound declaratively through [checkedOverride].
    effect(() => {
      const headerValue = this.selectAllHeaderValue();

      const buttons = this.radioButtons();
      if (buttons.length > 0) {
        const scopedCheckboxPrefix = `${this._selectAllScopeId}-select-all-header-checkbox-`;
        const scopedRadioPrefix = `${this._selectAllScopeId}-select-all-header-`;

        buttons.forEach((radio: any) => {
          const rowId = radio.name();
          const radioValue = radio.value();

          if (typeof rowId !== 'string') {
            return;
          }

          if (rowId.startsWith(scopedCheckboxPrefix)) {
            const columnId = rowId.slice(scopedCheckboxPrefix.length);
            radio.writeValue(this.isColumnSelectedForAll(columnId));
            return;
          }

          if (rowId.startsWith(scopedRadioPrefix)) {
            radio.writeValue(headerValue !== null && headerValue == radioValue);
          }
        });

        if (headerValue === null) {
          // Native radio groups can retain stale checked state after row/column changes; force clear.
          this.clearHeaderSelectAllSelection();
        }
      }
    });

    // Sync checkbox checked states with responses
    effect(() => {
      const cbs = this.checkboxInstances();
      const responses = this.rowResponses();
      if (cbs.length === 0) return;

      const cols = this.columns();
      let idx = 0;
      for (const section of untracked(this.sections)) {
        for (const row of section.rows) {
          const isVisible =
            row.showDefault == undefined ||
            row.showDefault ||
            !untracked(this.conditionalScore) ||
            untracked(this.shouldShowConditionalRows);
          if (!isVisible) continue;

          for (const col of cols) {
            if (idx >= cbs.length) break;
            const cb = cbs[idx];
            const rowVal = responses[row.linkId] as CheckboxRowValue | undefined;
            const isChecked = rowVal?.[col.id] === true;
            cb?.writeValue(isChecked ? [{ value: col.id, label: col.label }] : []);
            idx++;
          }
        }
      }
    });
  }

  // --- LIFECYCLE HOOKS ---

  /**
   * Apply the computed grid column widths after view initialization. This runs AFTER
   * HxTableHeaderDirective.ngAfterContentInit() (which calls setGridColumns()), ensuring our explicit column widths
   * always take precedence.
   */
  ngAfterViewInit(): void {
    const table = this.tableComponent();
    if (table) {
      table.updateGridColumns(this._buildGridWidths());
    }
  }

  /** Stored reference to the bound validator so it can be removed when this instance is destroyed. */
  private _boundValidateFn: ValidatorFn | null = null;

  // --- VALIDATION INTEGRATION ---
  ngOnInit() {
    if (this.ngControl && this.ngControl.control) {
      this._boundValidateFn = this.validate.bind(this);
      this.ngControl.control.addValidators(this._boundValidateFn);
      this.ngControl.control.updateValueAndValidity();
    }
    this.hiddenColumnIds.set(this._loadHiddenColumnIds());
  }

  ngOnDestroy() {
    // Remove this instance's validator when the component is destroyed (e.g. hidden by a dependency @if).
    // Without this, every hide→show cycle accumulates another stale validator on the form control.
    if (this.ngControl?.control && this._boundValidateFn) {
      this.ngControl.control.removeValidators(this._boundValidateFn);
      this.ngControl.control.updateValueAndValidity();
    }
  }

  validate(control: AbstractControl): ValidationErrors | null {
    return this.validationState().isValid
      ? null
      : { hxTableGrid: { valid: false, errors: this.validateTable().errors } };
  }

  @HostListener('focusout', ['$event'])
  onFocusOut(event: FocusEvent) {
    const relatedTarget = event.relatedTarget as HTMLElement;
    if (!this._elementRef.nativeElement.contains(relatedTarget)) {
      this.markAsTouched();
    }
  }

  // --- CONFIGURE COLUMNS ---

  private _columnConfigStorageKey(): string {
    const key = this.linkId() || this.tableId();
    return `symphony.tableGrid.${key}.hiddenColumns`;
  }

  /**
   * Reads persisted column config. Backward compatible with the legacy format (a plain array of hidden column ids) as
   * well as the current object format (`{hiddenColumnIds, columnWidths}`).
   */
  private _loadHiddenColumnIds(): Set<string> {
    if (typeof localStorage === 'undefined') return new Set();
    try {
      const raw = localStorage.getItem(this._columnConfigStorageKey());
      if (!raw) return new Set();
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) {
        return new Set(parsed.filter((id): id is string => typeof id === 'string'));
      }
      const hiddenColumnIds = parsed?.hiddenColumnIds;
      if (this.saveColumnWidths() && parsed?.columnWidths && typeof parsed.columnWidths === 'object') {
        this.columnWidthOverrides.set(parsed.columnWidths);
      }
      if (typeof parsed?.freezeHeader === 'boolean') {
        this.freezeHeaderOverride.set(parsed.freezeHeader);
      }
      return new Set(
        Array.isArray(hiddenColumnIds) ? hiddenColumnIds.filter((id): id is string => typeof id === 'string') : []
      );
    } catch {
      return new Set();
    }
  }

  private _persistColumnConfigState(): void {
    if (typeof localStorage === 'undefined') return;
    try {
      localStorage.setItem(
        this._columnConfigStorageKey(),
        JSON.stringify({
          hiddenColumnIds: Array.from(this.hiddenColumnIds()),
          columnWidths: this.saveColumnWidths() ? this.columnWidthOverrides() : {},
          freezeHeader: this.headerFrozen(),
        })
      );
    } catch {
      // Ignore storage failures (e.g. private browsing quota errors)
    }
  }

  /** Opens/closes the "Configure Columns" popover, positioning it under the given trigger element. */
  toggleColumnConfig(anchor: HTMLElement): void {
    if (this.showColumnConfig()) {
      this.showColumnConfig.set(false);
      return;
    }
    const rect = anchor.getBoundingClientRect();
    this.columnConfigPosition.set({ top: rect.bottom + 4, left: Math.max(0, rect.right - 240) });
    this.showColumnConfig.set(true);
  }

  @HostListener('document:click')
  closeColumnConfig(): void {
    this.showColumnConfig.set(false);
  }

  isColumnHidden(colId: string): boolean {
    return this.hiddenColumnIds().has(colId);
  }

  toggleColumnVisibility(colId: string): void {
    const hidden = new Set(this.hiddenColumnIds());
    if (hidden.has(colId)) {
      hidden.delete(colId);
    } else {
      hidden.add(colId);
    }
    this.hiddenColumnIds.set(hidden);
    this._persistColumnConfigState();
  }

  /** Toggles the pinned-header behaviour and persists the choice alongside the rest of the column config. */
  toggleFreezeHeader(): void {
    this.freezeHeaderOverride.set(!this.headerFrozen());
    this._persistColumnConfigState();
    this.freezeHeaderChange.emit(this.headerFrozen());
  }

  resetColumnConfig(): void {
    this.hiddenColumnIds.set(new Set());
    this.columnWidthOverrides.set({});
    this.freezeHeaderOverride.set(null);
    this._persistColumnConfigState();
  }

  /** Number of fixed leading header cells (serial/actions/question-label) before the data columns. */
  private _dataColumnsLeadingCount(): number {
    return (this.showSerialNumber() ? 1 : 0) + (this.showRowActions() ? 1 : 0) + 1;
  }

  /** Maps the grid widths reported after a drag-resize back onto data column ids, then persists them. */
  protected onColumnsResized(widths: string[]): void {
    if (!this.saveColumnWidths()) return;
    const dataWidths = widths.slice(this._dataColumnsLeadingCount());
    const visible = this.visibleColumns();
    const next = { ...this.columnWidthOverrides() };

    visible.forEach((col, index) => {
      const width = dataWidths[index];
      if (width) {
        next[col.id] = width;
      }
    });

    this.columnWidthOverrides.set(next);
    this._persistColumnConfigState();
  }

  // --- ACTIONS ---

  getRadioValue(rowId: string): PrimitiveValue | undefined {
    return this.rowResponses()[rowId] as PrimitiveValue | undefined;
  }

  /** Drives each cell radio's checked state, so it always reflects the stored response. */
  isRadioSelected(rowId: string, col: ColumnDefinition): boolean {
    const selected = this.rowResponses()[rowId];
    if (selected === undefined || selected === null) return false;
    // Loose compare: restored responses can be strings ('1') while column values are numbers (1).
    return selected == (col.value || col.id);
  }

  onRadioSelect(rowId: string, value: PrimitiveValue, event?: Event): void {
    if (this.disabledState()) return;
    // A click on the radio label reaches this host handler twice: once for the label and once for the click the
    // browser forwards to the native input. Only handle the input's click, otherwise the toggle below runs twice
    // and immediately clears the answer the user just made.
    if (event && !(event.target instanceof HTMLInputElement)) return;
    this.rowResponses.update((current) => {
      if (current[rowId] === value) {
        const next = { ...current };
        delete next[rowId];
        return next;
      }
      return { ...current, [rowId]: value };
    });
    this._propagateChanges();
  }

  getCheckboxValue(rowId: string, colId: string): boolean | undefined {
    const row = this.rowResponses()[rowId] as CheckboxRowValue | undefined;
    return row?.[colId];
  }

  onCheckboxChange(rowId: string, colId: string, checked: boolean): void {
    if (checked === undefined) return;
    if (this.disabledState()) return;
    this.rowResponses.update((current) => {
      const existingRow = (current[rowId] as CheckboxRowValue) || {};
      return {
        ...current,
        [rowId]: { ...existingRow, [colId]: checked },
      };
    });
    this._propagateChanges();
  }

  getTextValue(rowId: string, colId: string): string | undefined {
    const row = this.rowResponses()[rowId] as TextRowValue | undefined;
    return row?.[colId];
  }

  onTextInput(rowId: string, colId: string, event: Event): void {
    if (this.disabledState()) return;
    const target = event.target as HTMLInputElement;
    this.onTextChange(rowId, colId, target.value);
  }

  private onTextChange(rowId: string, colId: string, value: string): void {
    this.rowResponses.update((current) => {
      const existingRow = (current[rowId] as TextRowValue) || {};
      return {
        ...current,
        [rowId]: { ...existingRow, [colId]: value },
      };
    });
    this._propagateChanges();
  }

  private _propagateChanges(): void {
    const responses = this.rowResponses();
    const validation = this.validateTable();
    const score = this.totalScore();

    const output: ResponseOutput = {
      tableId: this.tableId(),
      responses: responses,
      valid: validation.valid,
      errors: validation.errors,
      total: score,
    };

    this.responseChange.emit(output);
    if (this._onChange) {
      // Propagate changes with totalScore included as 'value'
      this._onChange({
        responses: output.responses,
        value: score,
      });
    }
  }

  // --- VALIDATION STATE ---

  protected readonly validationState = computed(() => {
    const responses = this.rowResponses();
    const mode = this.requiredMode();
    const visibleRows = this.visibleRows(); // Use visible rows for validation

    const result = {
      isValid: true,
      rowErrorIds: new Set<string>(),
      tableErrorMessage: null as string | null,
    };

    if (mode === 'none') return result;

    if (mode === 'all-rows') {
      // All visible rows are required - no exceptions
      visibleRows.forEach((row) => {
        if (!this._hasRowValue(row.linkId, responses)) {
          result.rowErrorIds.add(row.linkId);
        }
      });
      if (result.rowErrorIds.size > 0) result.isValid = false;
    } else if (mode === 'custom') {
      // Only visible rows with explicit required: true are mandatory
      visibleRows.forEach((row) => {
        if (row.required === true && !this._hasRowValue(row.linkId, responses)) {
          result.rowErrorIds.add(row.linkId);
        }
      });
      if (result.rowErrorIds.size > 0) result.isValid = false;
    } else if (mode === 'at-least-one-yes') {
      const configuredTarget = this.validationTargetValue();
      const targetValue = this._resolveValidationTargetValue();
      let matchCount = 0;

      // Count how many visible rows match the target value
      visibleRows.forEach((row) => {
        const val = responses[row.linkId];
        if (this._valueMatchesTarget(val, targetValue)) {
          matchCount++;
        }
      });

      if (matchCount === 0) {
        result.isValid = false;
        result.tableErrorMessage = this.validationMessage() || `At least one ${configuredTarget || 'yes'} is required.`;
      }
    }

    return result;
  });

  shouldShowError(row: RowData): boolean {
    const isExternalTouched = this.ngControl ? this.ngControl.touched : false;
    const isInternalTouched = this.isTouched();
    const forceShow = this.forceShowErrors();

    // Show errors if forceShowErrors is true OR if touched
    if (!forceShow && !isInternalTouched && !isExternalTouched) return false;

    const state = this.validationState();
    const mode = this.requiredMode();

    if (mode === 'at-least-one-yes') {
      return !state.isValid;
    }
    return state.rowErrorIds.has(row.linkId);
  }

  // --- HELPERS ---

  private _hasRowValue(rowId: string, responses: TableResponses): boolean {
    const val = responses[rowId];
    return this._hasAnyValue(val);
  }

  private _hasAnyValue(val: any): boolean {
    if (typeof val === 'string') return val.trim() !== '';
    if (typeof val === 'number' || typeof val === 'boolean') return true;
    if (val && typeof val === 'object') {
      return Object.values(val).some((v) => v === true || (typeof v === 'string' && v !== ''));
    }
    return false;
  }

  private _resolveValidationTargetValue(): string {
    const configuredTarget = this.validationTargetValue();
    if (configuredTarget && configuredTarget.trim().length > 0) {
      return configuredTarget;
    }

    const affirmativeColumn = this.columns().find((col) => this._isAffirmativeColumn(col));
    if (!affirmativeColumn) {
      return 'yes';
    }

    if (this.inputType() === 'checkbox') {
      return affirmativeColumn.id;
    }

    return String(affirmativeColumn.value ?? affirmativeColumn.id);
  }

  private _isAffirmativeColumn(col: ColumnDefinition): boolean {
    return (
      this._isAffirmativeValue(col.value) || this._isAffirmativeString(col.id) || this._isAffirmativeString(col.label)
    );
  }

  private _isAffirmativeValue(value: unknown): boolean {
    if (typeof value === 'boolean') return value;
    if (typeof value === 'number') return value === 1;
    if (typeof value === 'string') return this._isAffirmativeString(value);
    return false;
  }

  private _isAffirmativeString(value: string): boolean {
    const normalized = value.toLowerCase().replace(/\s+/g, '');
    return (
      normalized === 'yes' ||
      normalized === 'y' ||
      normalized === 'true' ||
      normalized === '1' ||
      normalized.includes('yes')
    );
  }

  private _valueMatchesTarget(val: any, target: string): boolean {
    if (val === undefined || val === null) return false;
    if (typeof val === 'object') {
      return (val as Record<string, any>)[target] === true;
    }
    return String(val).toLocaleLowerCase() === target.toLocaleLowerCase();
  }

  isRowRequired(row: RowData): boolean {
    const mode = this.requiredMode();
    if (mode === 'none') return false;

    // For 'all-rows' mode: All rows are required
    if (mode === 'all-rows') {
      return true;
    }

    // For 'custom' mode: Only rows with explicit required: true are mandatory
    if (mode === 'custom') {
      return row.required === true;
    }

    return false;
  }

  markAsTouched(): void {
    if (!this.isTouched()) {
      this.isTouched.set(true);
      if (this._onTouched) this._onTouched();
      // Also mark the form control as touched if it exists
      if (this.ngControl && this.ngControl.control) {
        this.ngControl.control.markAsTouched();
      }
    }
  }

  isRowInvalid(row: RowData): boolean {
    if (!this.isRowRequired(row)) return false;
    const val = this.rowResponses()[row.linkId];
    if (this.inputType() === 'radio') {
      return val === undefined || val === null || val === '';
    }
    if (!val) return true;
    const valObj = val as Record<string, unknown>;
    return !Object.values(valObj).some((v) => v !== '' && v !== false && v !== null && v !== undefined);
  }

  validateTable(): ValidationResult {
    const errors: RowValidationError[] = [];
    const mode = this.requiredMode();
    const visibleRows = this.visibleRows(); // Use visible rows for validation

    if (mode === 'all-rows') {
      // All visible rows are required - validate every visible row
      visibleRows.forEach((row) => {
        if (this.isRowInvalid(row)) {
          errors.push({ rowId: row.linkId, message: 'Required' });
        }
      });
    } else if (mode === 'custom') {
      // Only visible rows with explicit required: true are mandatory
      visibleRows.forEach((row) => {
        if (row.required === true && this.isRowInvalid(row)) {
          errors.push({ rowId: row.linkId, message: 'Required' });
        }
      });
    }
    return { valid: errors.length === 0, errors };
  }

  // --- PAGE NAVIGATION METHODS ---

  /** Navigate to previous page */
  previousPage(): void {
    if (this.currentPage() <= 1 || this.disabledState()) {
      return;
    }
    const prevPage = this.currentPage();
    this.currentPage.set(prevPage - 1);
    this.emitPageNumberChangeEvent(prevPage);
  }

  /** Navigate to next page */
  nextPage(): void {
    if (this.currentPage() >= this.totalPages() || this.disabledState()) {
      return;
    }
    const prevPage = this.currentPage();
    this.currentPage.set(prevPage + 1);
    this.emitPageNumberChangeEvent(prevPage);
  }

  /** Navigate to specific page number */
  goToPage(pageNumber: number): void {
    if (!this.isValidPageNumber(pageNumber) || this.disabledState()) {
      return;
    }
    const prevPage = this.currentPage();
    this.currentPage.set(pageNumber);
    this.emitPageNumberChangeEvent(prevPage);
  }

  /** Handle page size selection change */
  onPageSizeChange(newPageSize: number): void {
    const currentPageSize = this.pageSize() ?? this.initialPageSize();
    if (this.disabledState() || newPageSize === currentPageSize) {
      return;
    }

    const prevPageSize = currentPageSize;
    const prevPage = this.currentPage();

    // Update page size and reset to page 1
    this.pageSize.set(newPageSize);
    this.currentPage.set(1);

    // Emit page size change event
    this.emitPageSizeChangeEvent(prevPage, prevPageSize);
  }

  /** Handle row reordering from drag and drop with connected drop lists */
  onRowDrop(event: CdkDragDrop<RowData[]>): void {
    if (!this.enableRowReordering()) return;

    // Within same section - reorder
    if (event.previousContainer === event.container) {
      moveItemInArray(event.container.data, event.previousIndex, event.currentIndex);
    }
    // Between different sections - transfer
    else {
      transferArrayItem(event.previousContainer.data, event.container.data, event.previousIndex, event.currentIndex);
    }

    // Emit the reorder event
    this.rowsReordered.emit(this.getAllRows());
  }

  /** Get list of connected drop list IDs (all other sections) */
  getConnectedDropLists(currentSectionIdx: number): string[] {
    return this.dropListIds().filter((_, idx) => idx !== currentSectionIdx);
  }

  /** Handle pagination event from HxPagination component */
  onPageChange(event: PaginationEvent): void {
    this.currentPage.set(event.newPage);
  }
  sectionHasVisibleRows(section: SectionData): boolean {
    const visibleRowIds = new Set(this.visibleRows().map((r) => r.linkId));
    return section.rows.some((row) => visibleRowIds.has(row.linkId));
  }

  // --- VALIDATION HELPERS ---

  /** Check if page number is valid */
  private isValidPageNumber(pageNumber: number): boolean {
    return pageNumber >= 1 && pageNumber <= this.totalPages();
  }

  // --- EVENT EMISSION ---

  /** Emit page number change event */
  private emitPageNumberChangeEvent(previousPage: number): void {
    const currentPageSize = this.pageSize() ?? this.initialPageSize();
    this.pageNumberChange.emit({
      newPage: this.currentPage(),
      previousPage,
      newPageSize: currentPageSize,
      previousPageSize: currentPageSize,
      totalPages: this.totalPages(),
    });
  }

  /** Emit page size change event */
  private emitPageSizeChangeEvent(previousPage: number, previousPageSize: number): void {
    this.pageSizeChange.emit({
      newPage: this.currentPage(),
      previousPage,
      newPageSize: this.pageSize() ?? this.initialPageSize(),
      previousPageSize,
      totalPages: this.totalPages(),
    });
  }

  override setDisabledState(isDisabled: boolean): void {
    super.setDisabledState(isDisabled);
  }

  override writeValue(value: TableResponses | { responses: TableResponses; value: number } | null): void {
    if (value) {
      // Handle new format with responses and value properties
      if (this._isNewFormat(value)) {
        this.rowResponses.set(value.responses);
      } else {
        // Handle legacy format (direct TableResponses)
        this.rowResponses.set(value);
      }
    } else {
      this.rowResponses.set({});
    }
    // Re-run the validator after rowResponses is updated. This is necessary because
    // ngOnInit calls updateValueAndValidity() before writeValue is invoked, so the
    // validator would otherwise see empty rowResponses and emit stale required errors
    // even when the form control already has saved responses (e.g. after hide→show).
    queueMicrotask(() => {
      if (this.ngControl?.control) {
        this.ngControl.control.updateValueAndValidity({ emitEvent: false });
      }
    });
  }

  /** Type guard to check if value is in new format with responses property */
  private _isNewFormat(
    value: TableResponses | { responses: TableResponses; value?: number }
  ): value is { responses: TableResponses; value?: number } {
    return (
      typeof value === 'object' &&
      value !== null &&
      'responses' in value &&
      typeof (value as any).responses === 'object' &&
      (value as any).responses !== null &&
      !Array.isArray((value as any).responses)
    );
  }
}
